import Title from "common/Typography/Title";
import Flex from "common/Boxes/Flex";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { PAGE_SPINNER_MARGIN_FROM_TOP } from "common/constants";
import { CSSProperties, ReactNode, useRef } from "react";
import { useTheme } from "styled-components";
import FiltersSection from "common/Filters/FiltersColumn/FiltersSection";
import { FiltersGroupProps } from "@shared/features/filters/types";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import LinkButton from "common/Buttons/LinkButton";
import useFiltersQuery from "common/Filters/common/useFiltersQuery";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Card from "common/Boxes/Card";
import CloseOutlined from "@ant-design/icons/CloseOutlined";

interface FiltersColumnProps extends FiltersGroupProps {
  width?: number;
  height?: string;
  expanded?: boolean;
  style?: CSSProperties;
  prefixNode?: ReactNode;
  closeMobileFilters?: () => void;
}

const FiltersColumn = ({
  selectLoadingStates,
  filtersSelectors,
  filtersActions,
  hideFilterTypes,
  isLoading,
  filterTypeToTitle,
  width,
  height,
  expanded,
  style,
  prefixNode,
  closeMobileFilters,
}: FiltersColumnProps) => {
  const filtersRef = useRef<HTMLDivElement>(null);
  const appTheme = useTheme();
  const dispatch = useDispatch();
  const filtersConfig = useSelector(filtersSelectors.selectFiltersConfig);
  const filtersData = useSelector(filtersSelectors.selectFiltersData);
  useFiltersQuery(filtersSelectors, filtersActions, hideFilterTypes);
  const { loading } = useSelector(
    selectLoadingStates ??
      (() => ({
        loading: false,
      }))
  );

  const filterTypes = filtersConfig.filterTypes?.filter(
    (filterType) =>
      filterType in filtersData && !hideFilterTypes?.includes(filterType)
  );

  const scrollbarWidth =
    (filtersRef.current?.offsetWidth || 0) -
    (filtersRef.current?.clientWidth || 0);

  const filtersContent = (
    <LoadingSpinnerOverlay
      isLoading={isLoading || loading}
      spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
      backgroundColor={appTheme.colors.cardBgColor}
    >
      {prefixNode}
      <FiltersWrapper>
        <Title type={"secondary"} level={3}>
          Filters
        </Title>
        <Flex>
          <LinkButton
            type="link"
            onClick={() => dispatch(filtersActions.selectAll(undefined))}
            style={{ padding: 0 }}
          >
            Reset
          </LinkButton>
          {!!closeMobileFilters && (
            <CloseOutlined
              onClick={closeMobileFilters}
              style={{ marginLeft: 12 }}
            />
          )}
        </Flex>
      </FiltersWrapper>
      <Flex $flexDirection="column">
        {filterTypes?.map((filterType) => {
          return (
            <FiltersSection
              filterType={filterType}
              filtersSelectors={filtersSelectors}
              filtersActions={filtersActions}
              key={filterType}
              filterTypeToTitle={filterTypeToTitle}
            />
          );
        })}
      </Flex>
    </LoadingSpinnerOverlay>
  );

  if (expanded !== undefined) {
    return (
      <ExpandCollapse expanded={expanded} horizontal={true} style={style}>
        <div style={{ width, height }}>
          <ExpandCollapseFiltersWrapper
            ref={filtersRef}
            $scrollbarWidth={scrollbarWidth}
            $width={width}
            $height={height}
          >
            {filtersContent}
          </ExpandCollapseFiltersWrapper>
        </div>
      </ExpandCollapse>
    );
  }

  return filtersContent;
};

export default FiltersColumn;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;
const ExpandCollapseFiltersWrapper = styled(Card)<{
  $scrollbarWidth: number;
  $height?: string;
  $width?: number;
}>`
  width: ${(p) => p.$width || 300}px;
  height: ${(p) => p.$height || "100%"};
  position: fixed;
  padding-bottom: 60px;
  padding-right: ${(p) => 16 - p.$scrollbarWidth}px;
  overflow-y: auto;
  &:not(:hover)::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
  &::-webkit-scrollbar {
    width: 8px !important;
  }
`;
